import { mapObjectFromSchema } from '../utilities/objectMapper';
import { PulseApi } from '../utilities/pulseApi.js';
import cloneDeep from 'lodash.clonedeep';

const timeoutDefaultMilliseconds = 60000; // 60 seconds
const defaultRetryOptions = { enableRetry: true, retryCount: 1 };

export class PulseBaseProvider {
    constructor(baseUrl, authId, secret, options = { timeout: timeoutDefaultMilliseconds, retryOptions: defaultRetryOptions, emToken: null }, logger = { error: function() {} }) {
        this.options = options;
        this.options.timeout = this.options.timeout ?? timeoutDefaultMilliseconds;
        this.options.retryOptions = this.options.retryOptions ?? defaultRetryOptions;
        this.logger = logger;
        this.baseUrl = baseUrl;
        this.authId = authId;
        this.secret = secret;

        this.postData = this.postData.bind(this);
        this.putData = this.putData.bind(this);

        this.pulseApi = new PulseApi(authId, secret, baseUrl, this.options.timeout, this.options.retryOptions, this.options.emToken);

        if (window && window.storage) {
            this.storage = window.storage;
        } else {
            this.storage = global.storage;
        }
    }

    async syncData(key, id, queryParams, uri, resolve, reject, responseValidationFunction, pulseApi, responseSourceToDestinationMap = null, unitConversionFunc, hmac=true, cacheDuration) {
        try {
            let newUri = uri;
            if (queryParams.pathParams) {
                Object.entries(queryParams.pathParams).forEach(([key, value]) => {
                    newUri = newUri + '/' + key + '=' + value;
                });

                delete queryParams.pathParams;
            }
            const response = await pulseApi.getData(newUri, queryParams, this.options.timeout, 'json', hmac);
            if(responseValidationFunction && !responseValidationFunction(response)) {
                const error = 'Response failed validation';
                reject(error);
                return error;
            }
            else if( !responseValidationFunction && (!response || response && !(response.r || response.result || response.Result))) {
                const error = 'Invalid response';
                reject(error);
                return error;
            }

            let result = response.r || response.result || response.Result || response;

            if (responseSourceToDestinationMap)
            {
                result = mapObjectFromSchema(responseSourceToDestinationMap, result);
            }

            result.responseTime = Date.now();

            if (unitConversionFunc) {
                // We must clone the result here because our unit conversion functions aren't always pure functions and if the promise from calling pulseApi was shared
                // from our debouncing logic between multiple converting provider callers then we will double-convert on the same object instance. Deep cloning the object
                // here like this prevents us from converting on the same object that was returned from the pulseApi promise result.
                const clonedResult = cloneDeep(result);
                result = unitConversionFunc(clonedResult);
            }

            this.storage.save({
                key,
                id,
                data: result,
                expires: cacheDuration
            });

            resolve(result);
            return result;
        } catch(error) {
            reject(error);
            return error;
        }
    }

    async postData(queryParams, data, uri, isJsonReq = true, responseValidationFunction = null, hmac=true)
    {
        const requestType = isJsonReq ? 'application/json' : null;
        let newUri = uri;
        if (queryParams.pathParams) {
            Object.entries(queryParams.pathParams).forEach(([key, value]) => {
                newUri = newUri + '/' + key + '=' + value;
            });

            delete queryParams.pathParams;
        }
        const response = await this.pulseApi.postData(newUri, queryParams, data, this.options.timeout, requestType, 'json', hmac);

        if (responseValidationFunction && !responseValidationFunction(response))
        {
            throw new Error('Pulse API response failed validation');
        } else if (!responseValidationFunction) {
            const hasResult = response && (response.r || response.result || response.Result || (response.code >= 200 && response.code < 300));
            if (!hasResult)
            {
                throw new Error('Response did not contain a valid Response object');
            }
        }

        const result = response.r || response.result || response.Result || response;
        result.responseTime = Date.now();

        return result;
    }

    async putData(queryParams, data, uri, isJsonReq = true, responseValidationFunction = null, hmac=true)
    {
        const requestType = isJsonReq ? 'application/json' : null;
        const response = await this.pulseApi.putData(uri, queryParams, data, this.options.timeout, requestType, 'json', hmac);

        if (responseValidationFunction && !responseValidationFunction(response))
        {
            throw new Error('Pulse API response failed validation');
        } else if (!responseValidationFunction) {
            const hasResult = response && (response.r || response.result || response.Result || (response.code >= 200 && response.code < 300));
            if (!hasResult)
            {
                throw new Error('Response did not contain a valid Response object');
            }
        }

        const result = response.r || response.result || response.Result || response;
        if (typeof result === 'object') {
            result.responseTime = Date.now();
        }

        return result;
    }

    async delete(queryParams, data, uri, isJsonReq = true, responseValidationFunction = null, hmac=true) {
        const requestType = isJsonReq ? 'application/json' : null;
        let newUri = uri;
        if (queryParams.pathParams) {
            Object.entries(queryParams.pathParams).forEach(([key, value]) => {
                newUri = newUri + '/' + key + '=' + value;
            });

            delete queryParams.pathParams;
        }

        const response = await this.pulseApi.delete(newUri, queryParams, data, this.options.timeout, requestType, 'json', hmac);

        if (responseValidationFunction && !responseValidationFunction(response)) {
            throw new Error('Pulse API response failed validation');
        } else if (!responseValidationFunction) {
            const hasResult = response && (response.r || response.result || response.Result || (response.code >= 200 && response.code < 300));
            if (!hasResult)
            {
                throw new Error('Response did not contain a valid Response object');
            }
        }

        const result = response.r || response.result || response.Result || result;
        if (result) {
            result.responseTime = Date.now();
        }

        return result;
    }

    async loadFromStorage(key, id, syncParams, autoSync=true, syncInBackground=false) {
        return await this.storage.load({
            key,
            id,
            autoSync,
            syncInBackground,
            syncParams: {
                key,
                pulseApi: this.pulseApi,
                ...syncParams
            }
        });
    }

    async saveToStorage(key, id, data) {
        return await this.storage.save({
            key
            , id
            , data
        });
    }

    removeFromStorage (key, id) {
        this.storage.remove({
            key
            ,id
        });
    }

    async readFromStorage(key, id) {
        // read data from storage
        return await this.storage.load({
            key: key
            , id: id
        });
    }
}

